import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { isDouyinAppChannel, isUnionPayAppChannel, getUnionPaySDK, channelNavigateTo } from '@/channels';
import { fetchCommonConfig } from '@/apis/home';
import { HOME } from '@/constants/path';
import { getCDNAssets } from '@/utils';
import { trackEvent } from '@/utils/tracker';

import type { PrivacyConfirmProps } from './Confirm';
import PrivacyConfirm from './Confirm';
import { clearPrivayPopCache, syncAndAgreePrivacy, getPrivacypopVersion } from './config';
import styles from './styles/page.module.less';

const POLICY_URL = 'https://www.mcd.cn/page.html?file=policy';

const PrivacyPage: FC = () => {
  const navigate = useNavigate();
  const [actionStyle, setActionStyle] = useState('1');

  const openUrl = (url: string) => {
    if (isDouyinAppChannel()) {
      channelNavigateTo({
        url: `/pages/web-view/index?uri=${encodeURIComponent(url)}`,
      });
    } else {
      window.open(url);
    }
  };
  const onPrivacyLink = useCallback<NonNullable<PrivacyConfirmProps['onPrivacyLink']>>((e) => {
    e.stopPropagation();

    openUrl(POLICY_URL);
  }, []);
  const onCustomPrivacyLink = useCallback<NonNullable<PrivacyConfirmProps['onCustomPrivacyLink']>>((url) => {
    if (url) {
      openUrl(url);
    }
  }, []);

  const onPrivacyConfirm = useCallback(async () => {
    try {
      trackEvent('privacyAgree', {
        belong_page: '隐私协议弹窗',
        button_name: '同意',
      });

      const _lastVersion = await syncAndAgreePrivacy();

      // for DEV. same as UnionPay miniapp channel
      if (process.env.NODE_ENV === 'development') {
        navigate(HOME, { replace: true });
      }

      if (isUnionPayAppChannel()) {
        navigate(HOME, { replace: true });
      } else if (isDouyinAppChannel()) {
        let _version = _lastVersion || '';
        if (!_version) {
          const _res = await fetchCommonConfig();
          if (_res?.config?.privacypopVersion) {
            _version = _res.config.privacypopVersion;
          }
        }
        channelNavigateTo({
          url: `/pages/privacy/index?privacypopVersion=${_version}`,
          redirect: true,
        });
      }
    } catch (error) {
      console.error('[PrivacyPage] onPrivacyConfirm fetch error.', error);
    }
  }, [navigate]);

  const onPrivacyExit = useCallback(() => {
    trackEvent('privacyDisagree', {
      belong_page: '隐私协议弹窗',
      button_name: '不同意并退出',
    });

    clearPrivayPopCache();

    if (isUnionPayAppChannel()) {
      getUnionPaySDK()?.closeWebApp();
    } else if (isDouyinAppChannel()) {
      channelNavigateTo({
        url: '/pages/privacy/index',
        redirect: true,
      });
    }
  }, []);

  useEffect(() => {
    void getPrivacypopVersion().then((res) => {
      if (res?.style) {
        setActionStyle(res.style);
      }
    });
  }, []);

  return (
    <div className={styles.container}>
      <img className={styles.imgLogo} src={getCDNAssets('privacy_icon_mcd.png')} />
      <div className={styles.mask}>
        <PrivacyConfirm
          className={styles.content}
          actionStyle={actionStyle}
          onPrivacyLink={onPrivacyLink}
          onCustomPrivacyLink={onCustomPrivacyLink}
          onConfirm={onPrivacyConfirm}
          onCancel={onPrivacyExit}
        />
      </div>
    </div>
  );
};

export default PrivacyPage;
